import type { PurchaseTickets } from '@/types/tickets'
import { SdkContext } from '../internal'
import type { ManagedPurchase, PurchaseRefund, RefundRequest, SalesFilters, SalesPage } from '@/types/sales'

/* F-55, the team's view of the community's sales. The community is the
   X-CommunityId's; owner, admin, finance and SuperAdmin, anyone else 403. */
const BASE = '/manage/purchases'

const cleanQuery = (params: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

export class SalesResource {
  constructor(private ctx: SdkContext) {}

  async list(params: SalesFilters & { page?: number; limit?: number } = {}): Promise<SalesPage> {
    const { refunded, ...rest } = params
    const result = await this.ctx.raw<ManagedPurchase[] | (Partial<SalesPage> & { data?: ManagedPurchase[] })>(
      { httpMethod: 'GET', path: BASE },
      { query: cleanQuery({ ...rest, ...(refunded ? { refunded: 'true' } : {}) }) }
    )
    if (Array.isArray(result)) {
      return { items: result, pagination: { page: 1, total: result.length, totalPages: 1 } }
    }
    const items = result?.items || result?.data || []
    return { items, pagination: result?.pagination || { page: 1, total: items.length, totalPages: 1 } }
  }

  /** One purchase with every refund (`refunds`, newest first). Another community's: 404. */
  async get(purchaseId: string): Promise<ManagedPurchase> {
    return this.ctx.call({ httpMethod: 'GET', path: `${BASE}/{purchaseId}` }, { purchaseId })
  }

  /**
   * 201 with the refund (SUCCEEDED, PENDING or AWAITING_BALANCE), 200 on a
   * retry with the same key. Refusals: 400 (boleto
   * without `settledOutside`, above what is left), 409 (nothing to refund,
   * pending or disputed purchase), 422 (the provider refused; the reason is
   * in the message).
   */
  /** F-116b: a group order's tickets (to pick which to refund). */
  async tickets(purchaseId: string): Promise<PurchaseTickets> {
    return this.ctx.call({ httpMethod: 'GET', path: `${BASE}/{purchaseId}/tickets` }, { purchaseId })
  }

  async refund(purchaseId: string, body: RefundRequest): Promise<PurchaseRefund> {
    return this.ctx.call({ httpMethod: 'POST', path: `${BASE}/{purchaseId}/refunds`, hasBody: true }, { purchaseId, body })
  }
}
